// Mirrors CarrierCommon.StatusCategory / STATUS_CATEGORY_RANK (netsuite repo,
// swm/carriers/swm_carrier_common.js) and the label wording in
// swm/helpers/swm_shipment_status_label_helpers.js — duplicated here since browser JS can't share a
// SuiteScript module. Keep all three in sync if category values or wording ever change.
const StatusCategory = {
    LABEL_CREATED: 1,
    PICKED_UP: 2,
    IN_TRANSIT: 3,
    OUT_FOR_DELIVERY: 4,
    DELIVERED: 5,
    DELIVERY_EXCEPTION: 6,
    RETURNED_TO_SENDER: 7,
    UNKNOWN: 8,
};

const STATUS_CATEGORY_RANK = {
    [StatusCategory.UNKNOWN]: 0,
    [StatusCategory.DELIVERY_EXCEPTION]: 1,
    [StatusCategory.RETURNED_TO_SENDER]: 2,
    [StatusCategory.LABEL_CREATED]: 3,
    [StatusCategory.PICKED_UP]: 4,
    [StatusCategory.IN_TRANSIT]: 5,
    [StatusCategory.OUT_FOR_DELIVERY]: 6,
    [StatusCategory.DELIVERED]: 7,
};

const STATUS_LABELS = {
    [StatusCategory.LABEL_CREATED]: 'Label Created',
    [StatusCategory.PICKED_UP]: 'Picked Up',
    [StatusCategory.IN_TRANSIT]: 'In Transit',
    [StatusCategory.OUT_FOR_DELIVERY]: 'Out for Delivery',
    [StatusCategory.DELIVERED]: 'Delivered',
    [StatusCategory.DELIVERY_EXCEPTION]: 'Delivery Exception',
    [StatusCategory.RETURNED_TO_SENDER]: 'Returned to Sender',
    [StatusCategory.UNKNOWN]: 'Tracking Update Pending',
};

// Every field below comes from NetSuite (customer/address/item text, carrier data) and is
// rendered via innerHTML, not textContent — escape before interpolating so a stray "<" or "&"
// in an address line, item memo, or carrier event description can't be parsed as markup.
function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (char) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
    }[char]));
}

// Tiered tracking-number display: live-tracking carriers (UPS/USPS/FedEx) and unresolved ones
// both link into our own tracking page (which itself explains when a carrier isn't supported);
// a known other carrier (freight/LTL — T-Force, DHL, XPO, etc., see
// swm_carrier_directory_helpers.js server-side) gets a direct link to their own tracking site
// instead, since we have no live data to show for those.
// Strictly false, not falsy: a Suitelet that predates the hasLiveTracking field sends nothing, and that
// must keep today's behavior (link into our tracking page) rather than silently turning every carrier,
// UPS included, into plain text. So the order the two halves get published in doesn't matter.
function hasNoLiveTracking(shipment) {
    return shipment.hasLiveTracking === false;
}

function trackingMarkup(shipment, ord, pwd) {
    if (!shipment.trackingNumbers.length) {
        return 'No tracking available';
    }

    if (shipment.directTrackingUrls && shipment.directTrackingUrls.length) {
        return shipment.directTrackingUrls
            .map((entry) => `<a href="${escapeHtml(entry.url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(entry.trackingNumber)}</a>`)
            .join(', ');
    }

    // No live tracking and no direct carrier URL (e.g. the "Other" carrier, or none set): our tracking
    // page would only say it can't track this carrier, so don't link there — just show the number(s).
    if (hasNoLiveTracking(shipment)) {
        return escapeHtml(shipment.trackingNumbers.join(', '));
    }

    const trackingPageUrl = `/pages/shipment-tracking?ord=${encodeURIComponent(ord)}&pwd=${encodeURIComponent(pwd)}&if=${encodeURIComponent(shipment.fulfillmentNumber)}`;
    return `<a href="${trackingPageUrl}">${escapeHtml(shipment.trackingNumbers.join(', '))}</a>`;
}

// Used both to build the id attribute below and to look the element back up via
// getElementById — must stay unescaped here (escaping only belongs at the point an id is
// embedded in HTML source, never in a value passed to getElementById, or the two would no
// longer agree).
function deliveryStatusElementId(fulfillmentNumber) {
    return `delivery-status-${fulfillmentNumber}`;
}

function deliveryStatusMarkup(shipment) {
    // Already Delivered per the stored (batch-updated) NetSuite value — a terminal state that
    // can't become un-delivered, so it's safe to trust without a live check.
    if (shipment.deliveryStatusCategory === StatusCategory.DELIVERED) {
        return `<span class="swm-label swm-label--success">${escapeHtml(shipment.deliveryStatusLabel)}</span>`;
    }

    // Nothing to look up live for these carriers (the polling job only covers UPS/USPS/FedEx), so
    // there's nothing to wait for: show what we have now, same as the live-lookup failure fallback.
    if (hasNoLiveTracking(shipment)) {
        return shipment.deliveryStatusLabel
            ? `<span class="swm-label swm-label--warning">${escapeHtml(shipment.deliveryStatusLabel)}</span>`
            : 'Shipped';
    }

    // Anything else might be stale — the internal carrier-polling Map/Reduce job can lag
    // real-world state by up to its polling window. Always show a loading placeholder and resolve
    // once to the live value, rather than showing the stored value first and having it visibly
    // flip — a customer watching a status change in front of them reads as "wait, which is it?"
    // even when the second value is the correct one. Falls back to the stored value only if the
    // live call itself fails (see lazyLoadDeliveryStatus's fallback()).
    const elementId = deliveryStatusElementId(shipment.fulfillmentNumber);
    return `<span id="${escapeHtml(elementId)}">Loading…</span>`;
}

// "Least advanced wins" across every package on one shipment — mirrors
// leastAdvancedStatusCategory() in swm_carrier_common.js: one delayed/early-stage package should
// hold back the whole shipment's displayed status.
function leastAdvancedCategory(categories) {
    return categories.reduce((leastAdvanced, category) =>
        STATUS_CATEGORY_RANK[category] < STATUS_CATEGORY_RANK[leastAdvanced] ? category : leastAdvanced
    );
}

// The tracking-lookup endpoint doesn't guarantee event order (see swm-shipment-tracking.js's own
// defensive sort) — same logic needed here to find each package's actual latest event.
function latestEventCategory(pkg) {
    if (!pkg.events || !pkg.events.length) return null;
    const sorted = pkg.events.slice().sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    return sorted[0].statusCategory;
}

// Which shipments get a live status lookup: not already Delivered, and a carrier we can actually look up.
function needsLiveStatus(shipment) {
    return shipment.deliveryStatusCategory !== StatusCategory.DELIVERED && !hasNoLiveTracking(shipment);
}

// Fire-and-forget per shipment. The placeholder is always "Loading…" now (see
// deliveryStatusMarkup), so a failed live call needs to actively fall back to the stored NetSuite
// value here — there's no stored value already sitting in the DOM to just leave alone anymore.
async function lazyLoadDeliveryStatus(shipment, ord, pwd) {
    const element = document.getElementById(deliveryStatusElementId(shipment.fulfillmentNumber));
    if (!element) return;

    const debugTag = `[delivery-status ${shipment.fulfillmentNumber}]`;

    function fallbackToStoredValue() {
        if (shipment.deliveryStatusLabel) {
            element.textContent = shipment.deliveryStatusLabel;
            element.className = 'swm-label swm-label--warning';
        } else {
            // No stored value either (e.g. a pre-tracking-era fulfillment with no carrier on
            // file) — "Shipped" is the one thing always truthfully known about any fulfillment
            // record, unlike StatusCategory.UNKNOWN's "Tracking Update Pending" wording, which
            // wrongly implies more info is coming for a case that will never resolve further.
            // Plain text, not a status badge, since we still don't actually know a live status.
            element.textContent = 'Shipped';
        }
    }

    try {
        const response = await fetch('/a/actions/tracking-lookup', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ord, pwd, if: shipment.fulfillmentNumber }),
        });

        const data = await response.json();
        console.debug(debugTag, 'response', data);

        if (!data.ok || !data.data || !data.data.ok) {
            console.debug(debugTag, 'falling back to stored value: outer or inner ok flag false');
            fallbackToStoredValue();
            return;
        }

        const packages = data.data.fulfillment.packages || [];
        const categories = packages.map(latestEventCategory).filter((category) => category != null);
        console.debug(debugTag, 'resolved categories', categories);

        if (!categories.length) {
            // Two different reasons can land here, and only one of them should fall back to the
            // (possibly stale) stored value: a real per-package carrier error means the live call
            // itself failed, so the stored value is the best available answer. But if every
            // package came back with events: [] and no error, that's a genuine, current "nothing
            // scanned yet" — live information that's fresher than the stored value, not a failure,
            // so it's shown directly rather than falling back to a possibly-outdated answer.
            if (packages.some((pkg) => pkg.error)) {
                console.debug(debugTag, 'falling back to stored value: a package-level carrier error occurred');
                fallbackToStoredValue();
            } else {
                console.debug(debugTag, 'no scans yet on any package; showing the live (not stored) Unknown state');
                element.textContent = STATUS_LABELS[StatusCategory.UNKNOWN];
                element.className = 'swm-label swm-label--warning';
            }
            return;
        }

        // Set the full class list rather than toggling — the element started as plain "Loading…"
        // text with no swm-label classes at all.
        const category = leastAdvancedCategory(categories);
        console.debug(debugTag, 'least-advanced category', category, STATUS_LABELS[category]);
        element.textContent = STATUS_LABELS[category] || STATUS_LABELS[StatusCategory.UNKNOWN];
        element.className = category === StatusCategory.DELIVERED
            ? 'swm-label swm-label--success'
            : 'swm-label swm-label--warning';
    } catch (error) {
        console.debug(debugTag, 'caught error, falling back to stored value', error);
        fallbackToStoredValue();
    }
}

// The email link carries the order number and password in the URL, but snippets/swm-strip-credentials.liquid
// (run in <head>, before any tracker) moves them into sessionStorage and cleans the address bar so
// analytics/ad scripts never receive the password. Read the URL first (in case that script didn't
// run), then the stored copy.
function readOrderLink() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('pwd')) {
        return { ord: params.get('ord'), pwd: params.get('pwd'), if: params.get('if') };
    }
    try {
        return JSON.parse(window.sessionStorage.getItem('swm-order-link')) || {};
    } catch (error) {
        return {};
    }
}

document.addEventListener('DOMContentLoaded', async () => {
    const { ord, pwd } = readOrderLink();

    const container = document.getElementById('js-order-status');

    function renderGenericError() {
        container.innerHTML = '<div class="alert alert--error alert--center">Error fetching data.</div>';
    }

    // Same generic message as every other failure path below — a link missing its query params
    // is functionally identical to a bad one from the customer's perspective.
    if (!ord || !pwd) {
        renderGenericError();
        return;
    }

    document.dispatchEvent(new CustomEvent("theme:loading:start"));

    try {
        const response = await fetch('/a/actions/order-lookup', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                ord,
                pwd
            }),
        });

        const data = await response.json();

        // Deliberately the same generic message as the catch block below for every failure kind
        // (bad order number, wrong password, malformed response, backend error) — no reason code
        // is shown to the customer, since nobody should be reaching this page other than via a
        // legitimate emailed link. This check just makes that failure path explicit instead of
        // relying on order being undefined and handleLayout throwing by accident.
        if (!data.ok || !data.data || !data.data.ok) {
            throw new Error('Order lookup failed: ' + (data.data && data.data.error));
        }

        const order = data.data.order;

        handleLayout(order);

        order.shipments
            .filter(needsLiveStatus)
            .forEach((shipment) => lazyLoadDeliveryStatus(shipment, ord, pwd));
    } catch (error) {
        renderGenericError();
    } finally {
        document.dispatchEvent(new CustomEvent("theme:loading:end"));
    }

    function handleLayout(order) {
        // The backend may omit the name and street lines (see SHOW_CUSTOMER_NAME_AND_STREET in the
        // order-lookup Suitelet), so every line is optional: nothing renders "undefined" or an empty row.
        const address = order.header.shippingAddress || {};
        const streetLines = [address.attention, address.addressee, address.addr1, address.addr2]
            .filter(Boolean)
            .map((line) => `${escapeHtml(line)}<br>`)
            .join('');
        const cityLine = [[address.city, address.state].filter(Boolean).join(', '), address.zip]
            .filter(Boolean)
            .map(escapeHtml)
            .join(' ');
        const orderAddress = `${streetLines}${cityLine}`;

        const shipmentCount = order.shipments.length;

        container.innerHTML = `
            <div class="grid">
                <div class="grid__cell 1/3--lap-and-up">
                    <div class="grid">
                        <div class="grid__cell 1/2--tablet swm-flex swm-pb-0">
                            <div class="card card--tighter 1/1">
                                <div class="card__header">
                                    <h2 class="h4 heading swm-knockout-31 swm-uppercase">Order Information</h2>
                                </div>
                                <div class="card__section">
                                    <table class="swm-table-meta">
                                        <tbody>
                                            <tr>
                                                <th scope="row">Order Number</th>
                                                <td>${escapeHtml(order.header.soNumber)}</td>
                                            </tr>
                                            <tr>
                                                <th scope="row">Order Date</th>
                                                <td>${escapeHtml(order.header.date)}</td>
                                            </tr>
                                            ${order.header.customerName ? `<tr>
                                                <th scope="row">Customer</th>
                                                <td>${escapeHtml(order.header.customerName)}</td>
                                            </tr>` : ''}
                                            <tr>
                                                <th scope="row">Order Status</th>
                                                <td>${escapeHtml(order.header.orderStatus)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                        <div class="grid__cell 1/2--tablet swm-flex swm-pb-0">
                            <div class="card card--tighter 1/1">
                                <div class="card__header">
                                    <h2 class="h4 heading swm-knockout-31 swm-uppercase">Shipping Address</h2>
                                </div>
                                <div class="card__section">
                                    <p>${orderAddress}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="grid__cell 2/3--lap-and-up">
                    ${
                        order.shipments.length
                            ? `
                                <div class="card card--tighter">
                                    <div class="card__header">
                                        <h2 class="h4 heading swm-knockout-31 swm-uppercase">Shipped Items</h2>
                                    </div>
                                    <div class="card__section">
                                        ${order.shipments.map((shipment, index) => `
                                            <article class="swm-fulfillment">
                                                <header>
                                                    <h3 class="h5 heading swm-knockout-31 swm-uppercase swm-m-0">Shipment ${index + 1}</h3>
                                                    <p class="swm-knockout-31 swm-uppercase">${escapeHtml(shipment.fulfillmentNumber)}</p>
                                                </header>
                                                <dl class="swm-fulfillment__meta">
                                                    <div>
                                                        <dt>Shipped</dt>
                                                        <dd>${escapeHtml(shipment.shipDate)}</dd>
                                                    </div>
                                                    <div>
                                                        <dt>Status</dt>
                                                        <dd>${deliveryStatusMarkup(shipment)}</dd>
                                                    </div>
                                                    <div>
                                                        <dt>Carrier</dt>
                                                        <dd>${escapeHtml(shipment.carrier || 'Unknown')}</dd>
                                                    </div>
                                                    <div>
                                                        <dt>Tracking</dt>
                                                        <dd>${trackingMarkup(shipment, ord, pwd)}</dd>
                                                    </div>
                                                </dl>
                                                <details ${shipmentCount === 1 ? 'open' : ''}>
                                                    <summary>
                                                        Shipment Items
                                                    </summary>
                                                    <div class="table-wrapper">
                                                        <table class="table table--tight">
                                                            <thead>
                                                                <tr>
                                                                    <th>Qty</th>
                                                                    <th>SKU</th>
                                                                    <th>Product</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                ${shipment.items.map(item => `
                                                                    <tr>
                                                                        <td class="swm-va-top">${escapeHtml(item.qty)}</td>
                                                                        <td class="swm-va-top">${escapeHtml(item.name)}</td>
                                                                        <td class="swm-va-top">${escapeHtml(item.description || '')}</td>
                                                                    </tr>
                                                                `).join('')}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </details>
                                            </article>
                                        `).join('')}
                                    </div>
                                </div>
                            `
                        : ''
                    }

                    ${
                        order.pendingItems.length
                            ? `
                                <div class="card card--tighter">
                                    <div class="card__header">
                                        <h2 class="h4 heading swm-knockout-31 swm-uppercase">Pending Items</h2>
                                    </div>
                                    <div class="card__section">
                                        <div class="table-wrapper">
                                            <table class="table table--tight">
                                                <thead>
                                                    <tr>
                                                        <th>Qty</th>
                                                        <th>SKU</th>
                                                        <th>Product</th>
                                                        <th>Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    ${order.pendingItems.map(item => `
                                                        <tr>
                                                            <td class="swm-va-top">${escapeHtml(item.qty)}</td>
                                                            <td class="swm-va-top">${escapeHtml(item.name)}</td>
                                                            <td class="swm-va-top">${escapeHtml(item.description || '')}</td>
                                                            <td class="swm-va-top">${escapeHtml(item.status)}</td>
                                                        </tr>
                                                    `).join('')}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            `
                        : ''
                    }

                    ${
                        order.cancelledItems.length
                            ? `
                                <div class="card card--tighter">
                                    <div class="card__header">
                                        <h2 class="h4 heading swm-knockout-31 swm-uppercase">Cancelled Items</h2>
                                    </div>
                                    <div class="card__section">
                                        <div class="table-wrapper">
                                            <table class="table table--tight">
                                                <thead>
                                                    <tr>
                                                        <th>Qty</th>
                                                        <th>SKU</th>
                                                        <th>Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    ${order.cancelledItems.map(item => `
                                                        <tr>
                                                            <td class="swm-va-top">${escapeHtml(item.qty)}</td>
                                                            <td class="swm-va-top">${escapeHtml(item.name)}</td>
                                                            <td class="swm-va-top">${escapeHtml(item.status)}</td>
                                                        </tr>
                                                    `).join('')}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            `
                        : ''
                    }
                </div>
            </div>
        `;
    }

});
